<svelte:options
  customElement={{
    tag: "oc-snackbar-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      open: { type: "Boolean" },
      message: { type: "String" },
      id: { type: "String", reflect: true },
      autoDismiss: { type: "String", attribute: "auto-dismiss" },
      forceReplaceExistingInstance: {
        type: "Boolean",
        attribute: "force-replace-existing-instance",
      },
    },
  }}
/>

<script lang="ts">
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { srSpeak } from "@otto-ec/global-resources/accesibility";
  import { onDestroy } from "svelte";
  import { safeId } from "@otto-ec/otto-components-utils/utils/safeId";
  import { useTimeout } from "@otto-ec/otto-components-utils/svelte/useTimeout";
  import { useTwoStageActivator } from "@otto-ec/otto-components-utils/svelte/twoStageActivator";
  import { useSlots } from "../../../common/utils/useSlots.svelte";
  import type { Events, Props } from "./SnackbarV1.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { useRestoreFocus } from "../../../common/utils/useRestoreFocus.svelte";

  let {
    open = false,
    message,
    id = safeId(),
    autoDismiss = true,
    forceReplaceExistingInstance = false,
  }: Props = $props();

  const Host = $host();
  const slots = useSlots(Host);
  const dispatch = useEventDispatcher<Events>(Host);

  let duration = 5000;
  let isHovered = $state(false);
  let isFocused = $state(false);

  const restoreFocus = useRestoreFocus(Host);

  const activator = useTwoStageActivator({ animated: () => Host.shadowRoot?.children });

  function handleClose(): void {
    open = false;
    restoreFocus();
    Host.blur();
  }

  function handleESCKey(event: KeyboardEvent): void {
    if (event.code === "Escape") {
      handleClose();
    }
  }

  $effect(() => {
    /*                                            */
    if (open) srSpeak(message, { type: "assertive" });
  });

  $effect(() => {
    if (open === activator.active) return;

    /*                                             */
    /*                                                     */
    if (
      !dispatch(open ? "oc-snackbar-open" : "oc-snackbar-close", undefined, {
        alias: [open ? "oc-open" : "oc-close"],
      })
    ) {
      /*                                            */
      open = activator.active;
      return;
    }

    activator.set(open);
  });

  $effect(() => {
    if (`${autoDismiss}` !== "false" && open && !isHovered && !isFocused)
      useTimeout(handleClose, duration);
  });

  /*                                                                                       */
  /*                                         */
  /*                                                                       */
  if (forceReplaceExistingInstance) {
    requestAnimationFrame(() => {
      document.querySelectorAll(`[id="${id}"]`).forEach((el) => el.remove());
    });
  }

  /*                                                                                              */
  /*                                                                                                       */
  if (import.meta.env.STORYBOOK) {
    /*                                                                                    */
    onDestroy(handleClose);
  }
</script>

<svelte:document onkeydown={handleESCKey} />

{#if activator.active}
  <div
    role="status"
    class="snackbar"
    class:snackbar--open={activator.ready}
    class:snackbar--has-actions={slots.actions}
    onmouseenter={() => (isHovered = true)}
    onmouseleave={() => (isHovered = false)}
    onfocusin={() => (isFocused = true)}
    onfocusout={() => (isFocused = false)}
    aria-hidden={activator.active ? "false" : "true"}
  >
    <div class="snackbar__body-actions-container">
      <div class="snackbar__body">
        {message}
      </div>

      {#if slots.actions}
        <div class="snackbar__actions" style:display={activator.active ? "block" : "none"}>
          <slot name="actions" />
        </div>
      {/if}

      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <oc-icon-button-v3
        class="snackbar__close-button"
        style:display={activator.active ? "block" : "none"}
        variant="transparent-inverted"
        icon="close"
        size={/*                      */ "50"}
        onclick={handleClose}
        oc-aria-label="Hinweis schließen"
      ></oc-icon-button-v3>
    </div>
  </div>

  <DefaultStyles />
{/if}

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  $icon-button-width: calc(tokens.$oc-base-dimension-relative-32 + 16px);

  :host {
    position: absolute;
  }

  .snackbar {
    background-color: tokens.$oc-component-snackbar-background-color;
    border-radius: tokens.$oc-component-snackbar-border-radius;
    bottom: tokens.$oc-component-snackbar-outer-spacing-bottom;
    filter: tokens.$oc-component-snackbar-shadow;
    left: 50%;
    min-height: 2rem;
    padding: tokens.$oc-component-snackbar-spacing-y $icon-button-width
      tokens.$oc-component-snackbar-spacing-y tokens.$oc-component-snackbar-spacing-left;
    position: fixed;
    width: calc(
      var(--oc-snackbar-container-width, 100vw) - tokens.$oc-component-snackbar-spacing-left -
        tokens.$oc-component-snackbar-outer-spacing-x * 2 - tokens.$oc-component-snackbar-gap-x -
        $icon-button-width
    );
    z-index: 9999;

    &__body-actions-container {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: tokens.$oc-component-snackbar-text-action-gap-y
        tokens.$oc-component-snackbar-text-action-min-gap-x;
      padding: tokens.$oc-component-snackbar-text-action-spacing-y 0;
    }

    &__body {
      color: tokens.$oc-component-snackbar-copy-color;
      font: tokens.$oc-component-snackbar-copy-font;
      padding-right: calc(tokens.$oc-component-snackbar-gap-x / 2);
    }

    &__actions {
      padding-right: calc(tokens.$oc-component-snackbar-gap-x / 2);
    }

    &__close-button {
      position: absolute;
      top: tokens.$oc-semantic-spacing-50;
      right: tokens.$oc-semantic-spacing-50;
      padding: 0;
      background: none;
      border: none;
    }

    &--has-actions {
      .snackbar__body {
        padding-right: 0;
      }
    }
  }

  .snackbar {
    transition: all tokens.$oc-component-snackbar-disappear-duration
      tokens.$oc-component-snackbar-disappear-easing;
    opacity: 0;
    transform: translate(-50%, 100%);
  }

  .snackbar--open {
    transition: all tokens.$oc-component-snackbar-appear-duration
      tokens.$oc-component-snackbar-appear-easing;
    opacity: 1;
    transform: translate(-50%, 0);
  }

  @include mixins.breakpoint-from-m(false, false) {
    .snackbar {
      max-width: max-content;
    }
  }
</style>
